import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { CATEGORIE } from "@/lib/api";
import { can, useAuth } from "@/lib/auth";
import { Search, Plus, Filter, FileSpreadsheet, FileText, FileDown } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function StockBadge({ p }) {
  if (p.giacenza === 0) return <span className="gm-badge gm-badge-danger">Esaurito</span>;
  if (p.giacenza <= p.quantita_minima) return <span className="gm-badge gm-badge-warning">Sotto scorta</span>;
  return <span className="gm-badge gm-badge-success">OK</span>;
}

export default function ProductsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const [sottoScorta, setSottoScorta] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (categoria) params.categoria = categoria;
      if (sottoScorta) params.sotto_scorta = true;
      const { data } = await api.get("/products", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [categoria, sottoScorta]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const exportExcel = () => {
    const rows = items.map(p => ({
      "Codice Interno": p.codice_interno,
      "Codice Fornitore": p.codice_fornitore,
      "Descrizione": p.descrizione,
      "Marca": p.marca,
      "Categoria": p.categoria,
      "Posizione": p.posizione,
      "Giacenza": p.giacenza,
      "Q.tà Min": p.quantita_minima,
      "Prezzo (€)": p.prezzo_acquisto,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prodotti");
    XLSX.writeFile(wb, `germac_prodotti_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const exportCSV = () => {
    const rows = [["Codice Interno","Codice Fornitore","Descrizione","Marca","Categoria","Posizione","Giacenza","Min","Prezzo"]];
    items.forEach(p => rows.push([p.codice_interno,p.codice_fornitore,p.descrizione,p.marca,p.categoria,p.posizione,p.giacenza,p.quantita_minima,p.prezzo_acquisto]));
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `germac_prodotti_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16); doc.text("GERMAC - Inventario Magazzino", 14, 15);
    doc.setFontSize(9); doc.text(`Generato: ${new Date().toLocaleString("it-IT")}`, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [["Codice Interno","Cod. Forn.","Descrizione","Marca","Categoria","Posizione","Giac.","Min","Prezzo €"]],
      body: items.map(p=>[p.codice_interno,p.codice_fornitore,p.descrizione,p.marca,p.categoria,p.posizione||"-",p.giacenza,p.quantita_minima,p.prezzo_acquisto?.toFixed(2)]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [255,204,0], textColor: 0 },
    });
    doc.save(`germac_inventario_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Inventario</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">Prodotti</h1>
        </div>
        {can(user, "create_product") && (
          <Link data-testid="products-new-button" to="/prodotti/nuovo" className="gm-btn-primary h-12 px-5 rounded-sm flex items-center gap-2">
            <Plus size={18} /> Nuovo Prodotto
          </Link>
        )}
      </div>

      <div className="gm-card p-4 mb-5">
        <form onSubmit={onSearch} className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              data-testid="products-search-input"
              value={q} onChange={(e)=>setQ(e.target.value)}
              placeholder="Cerca per codice, descrizione, marca, posizione..."
              className="gm-input pl-11"
            />
          </div>
          <select data-testid="products-category-filter" value={categoria} onChange={(e)=>setCategoria(e.target.value)} className="gm-input md:w-56">
            <option value="">Tutte le categorie</option>
            {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" onClick={()=>setSottoScorta(!sottoScorta)}
            data-testid="products-low-stock-filter"
            className={`h-12 px-4 border rounded-sm uppercase text-xs tracking-widest font-bold transition-colors ${
              sottoScorta ? "bg-[#FF9F0A] text-black border-[#FF9F0A]" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
            }`}>
            <Filter size={14} className="inline mr-2" /> Sotto scorta
          </button>
          <button type="submit" className="gm-btn-primary h-12 px-6 rounded-sm" data-testid="products-search-submit">Cerca</button>
        </form>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button onClick={exportExcel} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#4CAF50] flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800" data-testid="export-excel-button">
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button onClick={exportPDF} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#4CAF50] flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800" data-testid="export-pdf-button">
            <FileText size={14} /> PDF
          </button>
          <button onClick={exportCSV} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#4CAF50] flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800" data-testid="export-csv-button">
            <FileDown size={14} /> CSV
          </button>
        </div>
      </div>

      <div className="gm-card overflow-x-auto">
        {loading ? (
          <div className="p-8 text-zinc-500 uppercase tracking-widest text-sm">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-zinc-500 text-center">Nessun prodotto trovato.</div>
        ) : (
          <table className="gm-table">
            <thead>
              <tr>
                <th>Codice Interno</th>
                <th>Cod. Fornitore</th>
                <th>Descrizione</th>
                <th>Marca</th>
                <th>Categoria</th>
                <th>Posizione</th>
                <th className="text-right">Giac.</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} className="cursor-pointer" onClick={()=>nav(`/prodotti/${p.id}`)} data-testid="product-row">
                  <td className="font-mono-data text-[#4CAF50]">{p.codice_interno}</td>
                  <td className="font-mono-data text-zinc-300">{p.codice_fornitore}</td>
                  <td className="font-semibold">{p.descrizione}</td>
                  <td>{p.marca}</td>
                  <td className="text-zinc-400">{p.categoria}</td>
                  <td className="font-mono-data text-zinc-400">{p.posizione || "—"}</td>
                  <td className="text-right font-mono-data text-lg font-bold">{p.giacenza}</td>
                  <td><StockBadge p={p} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
